import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PageHeader, StatCard } from "@/components/Common";
import AutopilotQueue from "@/components/AutopilotQueue";
import { LangSwitcher } from "@/components/LangSwitcher";
import { ResellerSalesSummary } from "@/components/ResellerSalesSummary";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/i18n";
import {
  Radar, Users, Send, ShoppingCart, Store, Images, Map, Package,
  ArrowRight, Loader2, Building2, Newspaper,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";

function ModuleCard({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-start gap-4 rounded-xl border border-border bg-brema-card p-5 transition-colors duration-200 hover:border-primary/50"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brema-bg text-brand">
        <Icon className="h-5 w-5" />
      </span>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground">{title}</h3>
          <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 group-hover:text-brand" />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const openCampaigns = () => {
    setCampaignsOpen(true);
    if (!breakdown) api.get("/dashboard/campaigns-breakdown").then(({ data }) => setBreakdown(data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-brand" />
      </div>
    );
  }

  if (stats.role === "admin") {
    const chartData = Object.entries(stats.leads_by_status || {}).map(([k, v]) => ({ name: k, value: v }));
    return (
      <div>
        <PageHeader
          eyebrow="Plancia di comando Brand"
          title="Command Center"
          subtitle="Panoramica in tempo reale di lead generation, campagne di outreach e ambassador."
        />
        <div className="mb-6">
          <AutopilotQueue />
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Radar} label="Lead individuati" value={stats.leads_total} delay={0} to="/sales-agent" testid="stat-leads" />
          <StatCard icon={Building2} label="Qualificati" value={stats.leads_qualified} delay={0.05} to="/sales-agent?status=qualificato" testid="stat-qualified" />
          <StatCard icon={Send} label="Campagne inviate" value={stats.campaigns} delay={0.1} onClick={openCampaigns} testid="stat-campaigns" />
          <StatCard icon={Users} label="Ambassador" value={stats.ambassadors} delay={0.15} accent="red" to="/ambassadors" testid="stat-ambassadors" />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="rounded-xl border border-border bg-brema-card p-6 lg:col-span-2">
            <h3 className="mb-1 font-semibold text-foreground">Pipeline lead per stato</h3>
            <p className="mb-5 text-sm text-muted-foreground">Distribuzione dei negozi individuati dall'AI Sales Agent.</p>
            {chartData.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                Nessun lead ancora. Avvia una ricerca dall'AI Sales Agent.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    contentStyle={{ background: "#1D2433", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={theme === "dark" ? "#FACC15" : "#B91C1C"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="grid gap-4">
            <StatCard icon={Newspaper} label="Testate media" value={stats.media ?? 0} to="/media-finder" testid="stat-media" />
            <StatCard icon={Store} label="Rivenditori attivi" value={stats.resellers} to="/orders-admin" testid="stat-resellers" />
            <StatCard icon={ShoppingCart} label="Ordini B2B" value={stats.orders} accent="grey" to="/orders-admin" testid="stat-orders" />
          </div>
        </div>

        <h2 className="mb-4 mt-10 text-lg font-semibold text-foreground">Moduli</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <ModuleCard to="/sales-agent" icon={Radar} testid="module-sales"
            title="AI Sales Agent" desc="Trova negozi premium e genera outreach iper-personalizzato." />
          <ModuleCard to="/ambassadors" icon={Users} testid="module-ambassadors"
            title="Ambassador Finder" desc="Scopri micro-influencer su Instagram e Strava." />
          <ModuleCard to="/media-finder" icon={Newspaper} testid="module-media"
            title="Media & PR Finder" desc="Testate e giornalisti per categoria, con pitch e comunicati AI." />
          <ModuleCard to="/portal" icon={Store} testid="module-portal"
            title="Portale B2B" desc="Catalogo, ordini e materiali marketing." />
        </div>

        <Dialog open={campaignsOpen} onOpenChange={setCampaignsOpen}>
          <DialogContent data-testid="campaigns-breakdown-modal" className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Campagne inviate</DialogTitle>
              <DialogDescription>
                Riepilogo delle campagne effettivamente inviate per modulo{breakdown ? ` · ${breakdown.total} in totale` : ""}.
              </DialogDescription>
            </DialogHeader>
            {!breakdown ? (
              <div className="flex h-24 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
            ) : (
              <div className="space-y-2">
                {breakdown.modules.map((m) => (
                  <Link key={m.key} to={m.route} data-testid={`campaigns-row-${m.key}`} onClick={() => setCampaignsOpen(false)}
                    className="group flex items-center justify-between rounded-xl border border-border bg-brema-bg p-3 transition-colors hover:border-primary/50">
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      {m.label}
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-brand" />
                    </span>
                    <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-sm font-semibold text-brand" data-testid={`campaigns-count-${m.key}`}>{m.count}</span>
                  </Link>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Reseller dashboard
  const firstName = user?.name?.split(" ")[0] || "";
  return (
    <div>
      <PageHeader
        eyebrow={t("dash.hello", { name: firstName })}
        title={user?.shop_name || t("dash.shop_fallback")}
        subtitle={t("dash.subtitle")}
        actions={<LangSwitcher />}
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard icon={Package} label={t("dash.stat_products")} value={stats.products} />
        <StatCard icon={ShoppingCart} label={t("dash.stat_orders")} value={stats.orders} delay={0.05} />
        <StatCard icon={Images} label={t("dash.stat_assets")} value={stats.assets} delay={0.1} accent="grey" />
      </div>

      <h2 className="mb-4 mt-10 text-lg font-semibold text-foreground">{t("dash.quick_actions")}</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <ModuleCard to="/portal" icon={ShoppingCart} testid="quick-catalog"
          title={t("dash.quick_order_title")} desc={t("dash.quick_order_desc")} />
        <ModuleCard to="/portal?tab=marketing" icon={Images} testid="quick-marketing"
          title={t("dash.quick_mk_title")} desc={t("dash.quick_mk_desc")} />
      </div>

      <ResellerSalesSummary />
    </div>
  );
}
